import { inject, Injectable, signal } from '@angular/core';
import { map, MonoTypeOperatorFunction, Observable, tap } from 'rxjs';
import { Company, CompanyDetail, CompanyLight, CompanySearchResult, PostCompany, PostTenant, PutCompany, PutTenant, Tenant } from 'src/app/interfaces/api/company';
import { QueryParams } from 'src/app/interfaces/shared/query';
import { ApiService } from '../../core/api.service';

// Central resource with no socket sync: plain API calls, no RequestService cache.
@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private readonly apiService: ApiService = inject(ApiService);

  collection = 'Company';
  collectionPath = 'companies';

  // The last write on a company, by any page of this tab: the pages showing it reload it (no socket for companies).
  private readonly changedState = signal<{ id: string; version: number } | null>(null);
  readonly changed = this.changedState.asReadonly();
  private version = 0;

  //---GET---//
  searchAndCountCompanies(query: QueryParams = {}): Observable<CompanySearchResult> {
    return this.apiService.getCollection(this.collectionPath, 'searchAndCount', query);
  }

  getMyCompanies(): Observable<CompanyLight[]> {
    return this.apiService.getCollection(this.collectionPath, 'mine');
  }

  getCompany(id: string): Observable<CompanyDetail> {
    return this.fetchCompany(id);
  }

  // The tenants only (synthesis, current company check): no members, no project summaries.
  getCompanyTenants(id: string): Observable<CompanyDetail> {
    return this.fetchCompany(`${id}?view=tenants`);
  }

  // Lists always arrays: an older API or document may omit them (the tenants view does).
  private fetchCompany(path: string): Observable<CompanyDetail> {
    return this.apiService.getCollectionItem(this.collectionPath, '', path).pipe(
      map((company: CompanyDetail) => ({
        ...company,
        projects: company.projects ?? [],
        users: company.users ?? [],
        tenants: (company.tenants ?? []).map(tenant => ({ ...tenant, projects: tenant.projects ?? [] }))
      }))
    );
  }

  //---POST---//
  addCompany(company: PostCompany): Observable<Company> {
    return this.apiService.postCollectionData(this.collectionPath, '', company).pipe(tap((created: Company) => this.notify(created.id)));
  }

  attachProject(companyId: string, projectId: string): Observable<Company> {
    return this.apiService.postCollectionData(this.collectionPath, `${companyId}/projects`, { projectId }).pipe(this.changes(companyId));
  }

  attachUser(companyId: string, userId: string): Observable<Company> {
    return this.apiService.postCollectionData(this.collectionPath, `${companyId}/users`, { userId }).pipe(this.changes(companyId));
  }

  addTenant(companyId: string, tenant: PostTenant): Observable<Tenant> {
    return this.apiService.postCollectionData(this.collectionPath, `${companyId}/tenants`, tenant).pipe(this.changes(companyId));
  }

  //---PUT---//
  updateCompany(company: PutCompany): Observable<Company> {
    return this.apiService.updateCollectionItem(this.collectionPath, company).pipe(this.changes(company.id));
  }

  updateTenant(companyId: string, tenant: PutTenant): Observable<Tenant> {
    return this.apiService.updateCollectionItem(`${this.collectionPath}/${companyId}/tenants`, tenant).pipe(this.changes(companyId));
  }

  //---DELETE---//
  removeCompany(id: string): Observable<unknown> {
    return this.apiService.deleteCollectionItem(this.collectionPath, id).pipe(this.changes(id));
  }

  detachProject(companyId: string, projectId: string): Observable<Company> {
    return this.apiService.deleteCollectionItem(`${this.collectionPath}/${companyId}/projects`, projectId).pipe(this.changes(companyId));
  }

  detachUser(companyId: string, userId: string): Observable<Company> {
    return this.apiService.deleteCollectionItem(`${this.collectionPath}/${companyId}/users`, userId).pipe(this.changes(companyId));
  }

  removeTenant(companyId: string, tenantId: string): Observable<unknown> {
    return this.apiService.deleteCollectionItem(`${this.collectionPath}/${companyId}/tenants`, tenantId).pipe(this.changes(companyId));
  }

  private changes<T>(companyId: string): MonoTypeOperatorFunction<T> {
    return tap(() => this.notify(companyId));
  }

  private notify(companyId: string): void {
    this.changedState.set({ id: companyId, version: ++this.version });
  }
}
